import { Box, Typography } from "@mui/material"
import type {
  GridColDef,
  GridPaginationModel,
  GridRowClassNameParams,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-pro"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import { useCallback, useMemo, useRef, useState } from "react"
import { useNavDrawerOpen } from "../../../hooks/useNavDrawerOpen"
import { extraColors } from "../../../utils/theme"
import { BaseGrid } from "../../BaseGrid/BaseGrid"
import { PRECOMPUTED_PAGE_SIZE_OPTIONS } from "../constants"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import {
  getPrecomputedDrawerActiveRowClassName,
  precomputedGridSx,
} from "../PrecomputedSharedColumns"
import { PrecomputedSortControl } from "../PrecomputedSortControl"
import type { PrecomputedSortableColumn } from "../PrecomputedSortableColumns"
import type { PrecomputedTab } from "../types"
import { BiothreatV2AggregationStrip } from "./BiothreatV2AggregationStrip"
import { BiothreatV2ImpactSlider } from "./BiothreatV2ImpactSlider"
import {
  BiothreatV2ScanControls,
  BiothreatV2ScanMap,
} from "./BiothreatV2ScanMap"
import { BiothreatV2TopDiseases } from "./BiothreatV2TopDiseases"
import { V2_ACCENT, V2_EYEBROW, V2_SURFACE } from "./biothreatV2Tokens"
import { BiothreatV2GroupDrawer } from "./BiothreatV2GroupDrawer"
import { getGdeltImpactFloor, getImpact } from "./biothreatV2Signal"
import { groupSignalsByLocation, type LocationGroup } from "./biothreatV2Groups"
import { useSelectedDetailParam } from "./useSelectedDetailParam"

export interface BiothreatV2BodyProps {
  kind: PrecomputedTab
  /** Current grid page (server-paginated). */
  rows: BiothreatSignal[]
  /** Full filtered set (capped) for the rail summary (strip + Top Diseases). */
  summaryRows: BiothreatSignal[]
  /**
   * Full unfiltered "noise" set (capped) for the scan map — ignores the impact
   * floor so the sweep can reveal signal from noise. Falls back to summaryRows.
   */
  mapRows?: BiothreatSignal[]
  rowCount: number
  isLoading: boolean
  page: number
  onPageChange: (page: number) => void
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
  sortableColumns: PrecomputedSortableColumn[]
  defaultSortModel: GridSortModel
  columns: GridColDef<BiothreatSignal>[]
  /** Existing filter bar for the tab (reused, restyled by the container). */
  filters: React.ReactNode
  /** Renders the tab's detail drawer for the URL-selected row (or null). */
  renderDrawer: (
    signal: BiothreatSignal | null,
    onClose: () => void
  ) => React.ReactNode
}

export const BiothreatV2Body: React.FC<BiothreatV2BodyProps> = ({
  kind,
  rows,
  summaryRows,
  mapRows,
  rowCount,
  isLoading,
  page,
  onPageChange,
  sortModel,
  onSortModelChange,
  sortableColumns,
  defaultSortModel,
  columns,
  filters,
  renderDrawer,
}) => {
  const [mapOpen, setMapOpen] = useState(true)
  // True while the scan map is mid-pass (drives the on-demand status line).
  const [scanning, setScanning] = useState(false)
  const { pageSize, setPageSize, mapRailWidth, setMapRailWidth, gdeltFilters } =
    usePrecomputedBiothreatSignals()
  // When the app's left nav is collapsed, give the freed width (and height) to
  // the scan-map rail rather than the table.
  const { isOpen: navOpen } = useNavDrawerOpen()
  const mapHeight = navOpen ? 340 : 460
  // The map sweeps the full noise field; everything else stays on the filtered
  // summary set. NWSS has no noise query, so it falls back to summaryRows.
  const scanMapRows = mapRows ?? summaryRows

  // Rail width is draggable. A single local width is the source of truth (no
  // mid-drag fallback that could snap back); it's seeded from the persisted
  // value (or a nav-aware default) and re-committed to context on release.
  // Pointer moves are coalesced to one update per frame for a smooth drag.
  const RAIL_MIN = 280
  const RAIL_MAX = 760
  const [railWidth, setRailWidth] = useState(
    () => mapRailWidth ?? (navOpen ? 410 : 620)
  )
  const [resizing, setResizing] = useState(false)
  const resizeRef = useRef<{
    startX: number
    startWidth: number
    pendingX: number
    frame: number | null
  } | null>(null)

  const handleResizeStart = (e: React.PointerEvent) => {
    resizeRef.current = {
      startX: e.clientX,
      startWidth: railWidth,
      pendingX: e.clientX,
      frame: null,
    }
    setResizing(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const handleResizeMove = (e: React.PointerEvent) => {
    const state = resizeRef.current
    if (!state) return
    state.pendingX = e.clientX
    if (state.frame !== null) return
    state.frame = requestAnimationFrame(() => {
      const current = resizeRef.current
      if (!current) return
      current.frame = null
      const next = Math.min(
        RAIL_MAX,
        Math.max(
          RAIL_MIN,
          current.startWidth + (current.pendingX - current.startX)
        )
      )
      setRailWidth(next)
    })
  }
  const handleResizeEnd = (e: React.PointerEvent) => {
    const state = resizeRef.current
    if (state?.frame != null) cancelAnimationFrame(state.frame)
    resizeRef.current = null
    setResizing(false)
    setMapRailWidth(railWidth)
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  // Exactly one detail view is open at a time, tracked in the URL. Resolve the
  // selected signal row from the full filtered set (so map/Top-Diseases
  // selections off the current page still open), falling back to the page.
  const { selectedId, selectedGroupKey, openSignal, openGroup, close } =
    useSelectedDetailParam()
  const selectedRow =
    summaryRows.find(row => row.id === selectedId) ??
    rows.find(row => row.id === selectedId) ??
    null

  // The map renders the full GDELT noise field (no impact floor) so the sweep
  // can reveal signal from noise — but a dot's drawer must only show signals
  // that match the active filters. Filter to the impact floor before grouping,
  // so the group drawer (and the count===1 decision) reflect the filtered set.
  const impactFloor =
    kind === "gdelt" ? getGdeltImpactFloor(gdeltFilters.impactScoreMin) : null
  const groupRows = useMemo(
    () =>
      impactFloor == null
        ? scanMapRows
        : scanMapRows.filter(row => getImpact(row) >= impactFloor),
    [scanMapRows, impactFloor]
  )
  const mapGroups = useMemo(
    () => groupSignalsByLocation(groupRows, kind),
    [groupRows, kind]
  )
  const selectedGroup = selectedGroupKey
    ? (mapGroups.find(group => group.key === selectedGroupKey) ?? null)
    : null

  // Clicking a dot opens the group drawer; a single-signal dot skips straight to
  // the signal drawer (no empty group view). Opening any signal replaces the
  // open detail view — drilling from the group list into a signal swaps the
  // group drawer for the signal drawer (one detail view at a time). Resolve the
  // clicked dot to its FILTERED group so the count reflects matching signals.
  const onSelectGroup = useCallback(
    (clicked: LocationGroup) => {
      // Resolve the clicked dot to its FILTERED group. A dot whose signals are
      // all below the active floor has no entry here (it is a sweep-reveal
      // "noise" dot the filter hides); ignore the click rather than opening an
      // empty group/signal detail that the selection lookups can't resolve.
      const group = mapGroups.find(g => g.key === clicked.key)
      if (!group) return
      if (group.count === 1) openSignal(group.lead.id)
      else openGroup(group.key)
    },
    [mapGroups, openGroup, openSignal]
  )
  const onRowClick = useCallback(
    (row: BiothreatSignal) => openSignal(row.id),
    [openSignal]
  )

  const handleRowClick = (params: GridRowParams<BiothreatSignal>) =>
    onRowClick(params.row)

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<BiothreatSignal>) =>
      getPrecomputedDrawerActiveRowClassName(params, selectedId),
    [selectedId]
  )

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/* Toolbar — the hero band that governs everything below. Brief:
          flex, align-end, gap 18px, padding 26px 22px 22px. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: "18px",
            rowGap: "16px",
            pt: "26px",
            px: "22px",
            pb: "22px",
            borderBottom: `1px solid ${V2_SURFACE.hairlineStrong}`,
            background: "rgba(8,11,17,0.4)",
          }}
        >
          {filters}
          <PrecomputedSortControl
            sortModel={sortModel}
            onChange={onSortModelChange}
            sortableColumns={sortableColumns}
            defaultSortModel={defaultSortModel}
          />
        </Box>

        {kind === "gdelt" ? (
          <BiothreatV2ImpactSlider mapRows={scanMapRows} />
        ) : null}

        <BiothreatV2AggregationStrip
          rows={summaryRows}
          kind={kind}
          mapOpen={mapOpen}
          onToggleMap={() => setMapOpen(open => !open)}
        />

        <Box
          sx={{
            display: "flex",
            gap: 2,
            p: 3,
            minWidth: 0,
            // Avoid text selection flicker while dragging the rail handle.
            userSelect: resizing ? "none" : "auto",
          }}
        >
          {mapOpen ? (
            <Box
              sx={{
                width: railWidth,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                // Snap 1:1 with the pointer while dragging; ease other changes.
                transition: resizing ? "none" : "width 0.15s ease",
              }}
            >
              {/* Map section header — scope label (show/hide lives in the strip). */}
              <Typography sx={{ ...V2_EYEBROW, color: extraColors.white }}>
                Geospatial Scan ·{" "}
                <Box component="span" sx={{ color: V2_ACCENT.cyan }}>
                  {kind === "nwss" ? "NWSS · CONUS" : "OSINT · Global"}
                </Box>
              </Typography>
              <BiothreatV2ScanMap
                rows={scanMapRows}
                kind={kind}
                selectedId={selectedId}
                selectedGroupKey={selectedGroupKey}
                onSelectGroup={onSelectGroup}
                height={mapHeight}
                containerWidth={railWidth}
                onScanningChange={setScanning}
              />
              <BiothreatV2ScanControls kind={kind} scanning={scanning} />
              <BiothreatV2TopDiseases
                rows={summaryRows}
                kind={kind}
                onSelect={onRowClick}
              />
            </Box>
          ) : null}

          {mapOpen ? (
            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize scan map"
              onPointerDown={handleResizeStart}
              onPointerMove={handleResizeMove}
              onPointerUp={handleResizeEnd}
              onPointerCancel={handleResizeEnd}
              sx={{
                flexShrink: 0,
                width: "6px",
                alignSelf: "stretch",
                borderRadius: "3px",
                cursor: "col-resize",
                touchAction: "none",
                backgroundColor: resizing
                  ? V2_ACCENT.cyan
                  : V2_SURFACE.hairline,
                transition: "background-color 120ms ease",
                "&:hover": { backgroundColor: V2_ACCENT.cyan },
              }}
            />
          ) : null}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <BaseGrid
              loading={isLoading}
              rows={rows}
              columns={columns}
              checkbox={false}
              density="compact"
              treeData={false}
              // The page scrolls, so the grid grows to fit its rows rather than
              // scrolling internally — keeps pagination reachable below the rows.
              autoHeight
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              onRowClick={handleRowClick}
              pagination
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model: GridPaginationModel) => {
                if (model.pageSize !== pageSize) {
                  setPageSize(model.pageSize)
                  onPageChange(0)
                } else {
                  onPageChange(model.page)
                }
              }}
              rowCount={rowCount}
              pageSizeOptions={PRECOMPUTED_PAGE_SIZE_OPTIONS}
              getRowHeight={() => 72}
              getRowClassName={getRowClassName}
              sx={{
                ...precomputedGridSx,
                minHeight: 480,
                // The whole page scrolls, not the grid. autoHeight renders all
                // rows; here we unset every height/overflow that would
                // otherwise make the inner virtual-scroller a scroll region
                // (the styled grid forces `overflowY: scroll !important`, so we
                // override that exact longhand). Vertical scroll falls through
                // to the page; horizontal stays available for wide columns.
                height: "auto !important",
                overflowY: "visible !important",
                "& > div": {
                  height: "auto !important",
                  overflowY: "visible !important",
                },
                "& .MuiDataGrid-root": { height: "auto !important" },
                "& .MuiDataGrid-main": { overflowY: "visible !important" },
                "& .MuiDataGrid-virtualScroller": {
                  height: "auto !important",
                  overflowY: "visible !important",
                },
                // BaseGrid's styled grid sets `height: inherit` here, which
                // clips the rendered rows into a scroll region — unset it so
                // the zone grows to its content.
                "& .MuiDataGrid-virtualScrollerRenderZone": {
                  height: "auto !important",
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Typography color={extraColors.hint} sx={{ p: 4 }}>
                    No results found.
                  </Typography>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>
      {/* Exactly one detail view is open at a time (enforced by the URL): the
          signal drawer when a signal is selected, otherwise the group drawer.
          Selecting a signal from the group list swaps one for the other. */}
      {renderDrawer(selectedRow, close)}
      <BiothreatV2GroupDrawer
        group={selectedRow ? null : selectedGroup}
        kind={kind}
        onSelectSignal={onRowClick}
        onClose={close}
        variant="command"
      />
    </>
  )
}
